import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ExportButtons from './components/ExportButtons';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import FiltersBar from './components/FiltersBar';
import HelpButton from '../../help/HelpButton';

function fmtSecs(n) {
  if (n == null) return '—';
  const s = Number(n) || 0;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

export default function AgentProductivity() {
  const [range, setRange, apiParams] = useRangeState('today');
  const [filters, setFilters] = useState({});
  const merged = { ...apiParams, ...filters };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'agents', merged],
    queryFn: () => reportsApi.agents(merged),
    // Live "today" view refreshes every 30s; historical stays static.
    refetchInterval: range.preset === 'today' ? 30_000 : false,
    keepPreviousData: true,
  });

  // 2026-10-02 (Helen/Ops): hide the "Missed" column on-screen for now to avoid
  // confusing supervisors (it counts ring-outs mostly recovered by teammates /
  // caused by softphone glitches). Keep only "Lost (caller hung up)" = the
  // misses that actually lost a customer. Backend still computes missed_calls
  // (so it's available in exports and a 1-line revert here re-shows it).
  const HIDDEN_COLS = ['missed_calls'];
  const defaultHeaders = ['extension','agent_name','inbound_calls','outbound_calls','internal_calls','total_calls','missed_calls','lost_calls','total_talk_sec','avg_handle_sec','inbound_aht_sec','outbound_aht_sec','first_call_at','last_call_at','_clusters'];
  const displayHeaders = (data?.headers || defaultHeaders).filter((h) => !HIDDEN_COLS.includes(h));

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <FiltersBar value={filters} onChange={setFilters} show={['agent','queue','direction']} />

      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">{data?.title || 'Agent productivity'}</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-muted">{data?.subtitle}</span>
          <HelpButton moduleKey="reports" />
          <ExportButtons report="agents" params={merged} disabled={isLoading} />
          <button
            type="button"
            onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
            title="Refresh"
          >
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      {isError && (
        <div className="text-sm text-red-600 dark:text-red-300 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      {data?.meta && (
        <div className="text-xs text-surface-muted mb-2 flex flex-wrap gap-x-3">
          {data.meta.source_clusters?.length > 0 && (
            <span>Data from: <strong>{data.meta.source_clusters.join(', ')}</strong></span>
          )}
          {data.meta.latest_datapoint && (
            <span>Latest CDR row: <strong>{data.meta.latest_datapoint}</strong></span>
          )}
          {data.meta.failed_clusters?.length > 0 && (
            <span className="text-accent-red">⚠ partial: failed on {data.meta.failed_clusters.map(f=>f.cluster).join(', ')}</span>
          )}
        </div>
      )}

      <ReportTable
        headers={displayHeaders}
        rows={data?.rows || []}
        columnLabels={{
          extension: 'Ext / CallerID',
          agent_name: 'Agent',
          inbound_calls: 'Inbound',
          outbound_calls: 'Outbound',
          internal_calls: 'Internal',
          total_calls: 'Total',
          missed_calls: 'Missed',
          lost_calls: 'Lost (caller hung up)',
          total_talk_sec: 'Total talk',
          avg_handle_sec: 'AHT',
          inbound_aht_sec: 'Inbound AHT',
          outbound_aht_sec: 'Outbound AHT',
          first_call_at: 'First call',
          last_call_at: 'Last call',
          _clusters: 'DC',
        }}
        columnTooltips={{
          lost_calls: 'Calls that rang this agent, were not answered, and the caller ultimately hung up with NO agent ever answering. These are the calls that actually lost a customer.',
        }}
        formatters={{
          total_talk_sec: fmtSecs,
          avg_handle_sec: fmtSecs,
          inbound_aht_sec: fmtSecs,
          outbound_aht_sec: fmtSecs,
        }}
      />
    </div>
  );
}
